import { useState, useEffect, useRef, useCallback } from "react";
import API from "../../../../config/api";
import { useTranslation } from "../../../../i18n/LanguageProvider";
import { getWomanThumbImageUrl } from "../../../../utils/imageUrl";
import "./Minigame.sass";

export default function Minigame({ initialGameData }) {
    const { language, t } = useTranslation();
    const [gameData, setGameData] = useState(null);
    const [gameKey, setGameKey] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [correctAnswer, setCorrectAnswer] = useState(null);
    const [answered, setAnswered] = useState(false);
    const [fading, setFading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [playerName, setPlayerName] = useState("");
    const [submitStatus, setSubmitStatus] = useState(null);
    const initialDataUsed = useRef(false);
    const [isBanned, setIsBanned] = useState(() => {
        return localStorage.getItem("minigameBanned") === "true";
    });
    const [score, setScore] = useState(() => {
        const saved = localStorage.getItem("minigameScore");
        return saved ? parseInt(saved, 10) : 0;
    });
    const [lastSubmittedScore, setLastSubmittedScore] = useState(() => {
        const saved = localStorage.getItem("minigameLastSubmittedScore");
        return saved ? parseInt(saved, 10) : null;
    });

    // Check if user is banned on mount
    useEffect(() => {
        fetch(`${API.result}?checkBan=true`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.banned) {
                    setIsBanned(true);
                    localStorage.setItem("minigameBanned", "true");
                }
            })
            .catch((err) => console.error("Error checking ban:", err));
    }, []);

    const handleSubmitScore = async () => {
        if (!playerName.trim() || isBanned) return;

        // Check if this score was already submitted
        if (lastSubmittedScore === score) {
            setSubmitStatus({ type: "error", message: t("minigame.submitted") });
            setTimeout(() => setSubmitStatus(null), 3000);
            return;
        }

        try {
            const response = await fetch(API.result, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name: playerName.trim(),
                    score: score,
                }),
            });

            const result = await response.json();

            if (result.banned) {
                setIsBanned(true);
                localStorage.setItem("minigameBanned", "true");
                setSubmitStatus({ type: "error", message: result.error });
            } else if (result.success) {
                setSubmitStatus({ type: "success", message: result.message });
                setLastSubmittedScore(score);
                localStorage.setItem("minigameLastSubmittedScore", score.toString());
                setTimeout(() => setSubmitStatus(null), 3000);
            } else {
                setSubmitStatus({ type: "error", message: result.error });
            }
        } catch (err) {
            console.error("Error submitting score:", err);
            setSubmitStatus({ type: "error", message: t("minigame.sendError") });
        }
    };

    const fetchGame = useCallback(() => {
        setLoading(true);
        setCorrectAnswer(null);
        fetch(`${API.minigame}?lang=${language}`, {
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => {
                setGameData(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching game:", err);
                setLoading(false);
            });
    }, [language]);

    useEffect(() => {
        // Use initial data on first load, fetch new data on subsequent games
        if (!initialDataUsed.current && initialGameData) {
            setGameData(initialGameData);
            setLoading(false);
            initialDataUsed.current = true;
        } else if (initialDataUsed.current || !initialGameData || gameKey > 0) {
            fetchGame();
        }
    }, [fetchGame, gameKey, initialGameData]);

    useEffect(() => {
        localStorage.setItem("minigameScore", score);
    }, [score]);

    const resetGame = () => {
        setSelectedOption(null);
        setCorrectAnswer(null);
        setAnswered(false);
        setFading(false);
        setGameKey((prev) => prev + 1);
    };

    const handleOptionClick = async (optionIndex) => {
        if (answered || !gameData) return;

        setSelectedOption(optionIndex);
        setAnswered(true);

        // Check answer with server
        try {
            const response = await fetch(
                `${API.minigame}?checkAnswer=true&gameId=${gameData.gameId}&answer=${optionIndex}`,
                { credentials: "include" },
            );
            const result = await response.json();

            setCorrectAnswer(result.correctAnswer);

            if (result.correct) {
                setScore((prev) => prev + 1);
            }
        } catch (err) {
            console.error("Error checking answer:", err);
        }

        setTimeout(() => {
            setFading(true);
            setTimeout(() => {
                resetGame();
            }, 500);
        }, 1500);
    };

    const getOptionClass = (optionIndex) => {
        if (!answered || correctAnswer === null) return "option";
        if (optionIndex === correctAnswer) return "option correct";
        if (optionIndex === selectedOption && optionIndex !== correctAnswer) return "option incorrect";
        return "option";
    };

    if (loading || !gameData) {
        return (
            <div className="minigame card-common">
                <div className="minigame__card">
                    <div className="content">
                        <h2 className="question">{t("minigame.loading")}</h2>
                    </div>
                </div>
            </div>
        );
    }

    const { type, question, options, image } = gameData;

    return (
        <div key={gameKey} className={`minigame card-common type-${type} ${fading ? "fading" : "fade-in"}`}>
            <div className="minigame__card">
                {(type === 1 || type === 3) && image && (
                    <div
                        className="image"
                        style={{ backgroundImage: `url(${getWomanThumbImageUrl(image)})` }}
                    />
                )}
                <div className="content">
                    <h2 className="question">{question}</h2>
                    <div className={`options ${type === 2 ? "options--images" : ""}`}>
                        {options.map((option, index) => {
                            return (
                                <div
                                    key={index}
                                    className={getOptionClass(index)}
                                    onClick={() => handleOptionClick(index)}>
                                    {type === 2 ? (
                                        <div
                                            className="option-image"
                                            style={{
                                                backgroundImage: `url(${getWomanThumbImageUrl(option)})`,
                                            }}
                                        />
                                    ) : (
                                        option
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="minigame__score">
                <div className="your-score">
                    {t("minigame.score")} <span className="score-number">{score}</span> {t("minigame.wins")}
                </div>
                {/* {isBanned ? (
                    <div className="submit banned">
                        <span className="banned-message">{t("minigame.banned")}</span>
                    </div>
                ) : (
                    <div className="submit">
                        <input
                            type="text"
                            placeholder={t("minigame.namePlaceholder")}
                            value={playerName}
                            onChange={(e) => setPlayerName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSubmitScore()}
                        />
                        <button onClick={handleSubmitScore} disabled={!playerName.trim()}>
                            {t("minigame.submit")}
                        </button>
                        {submitStatus && (
                            <span className={`submit-status ${submitStatus.type}`}>{submitStatus.message}</span>
                        )}
                    </div>
                )} */}
            </div>
        </div>
    );
}
